// (c) chaehongee. Confidential and proprietary.

import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ')

/**
 * Dialog a11y for modal overlays: focus trap + Escape-to-close + focus return.
 *
 * Usage: attach the returned ref to the modal *panel* (the surface, not the
 * scrim) and give that panel `role="dialog" aria-modal="true"`,
 * `aria-labelledby` pointing at the title, and `tabIndex={-1}` so the panel
 * itself can receive focus when it holds no focusable children.
 *
 *   const { dialogRef } = useModalDialog(isOpen, onClose)
 *   ...
 *   <div className={styles.overlay} onClick={onClose}>
 *     <div ref={dialogRef} role="dialog" aria-modal="true"
 *          aria-labelledby="myTitle" tabIndex={-1}
 *          className={styles.modal} onClick={(e) => e.stopPropagation()}>
 *
 * When adopting this, delete the modal's own window `keydown`/Escape effect —
 * this hook owns Escape. Backdrop-click dismissal stays on the overlay.
 */
export function useModalDialog<T extends HTMLElement = HTMLDivElement>(
    isOpen: boolean,
    onClose: () => void,
): { dialogRef: RefObject<T | null> } {
    const dialogRef = useRef<T | null>(null)
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    useEffect(() => {
        if (!isOpen) return
        const dialog = dialogRef.current
        const previouslyFocused = document.activeElement as HTMLElement | null

        const visibleFocusables = (): HTMLElement[] =>
            dialog
                ? Array.from(
                      dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
                  ).filter((el) => el.offsetParent !== null)
                : []

        // Move focus into the dialog on open.
        const initial = visibleFocusables()
        ;(initial[0] ?? dialog)?.focus()

        const onKeyDown = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                onCloseRef.current()
                return
            }
            if (e.key !== 'Tab' || !dialog) return
            const items = visibleFocusables()
            if (items.length === 0) {
                e.preventDefault()
                dialog.focus()
                return
            }
            const first = items[0]
            const last = items[items.length - 1]
            const active = document.activeElement
            if (e.shiftKey && active === first) {
                e.preventDefault()
                last.focus()
            } else if (!e.shiftKey && active === last) {
                e.preventDefault()
                first.focus()
            }
        }

        // Listen on window (the outermost target): real key events bubble up
        // through document to window, and this also catches events dispatched
        // directly at window (e.g. fireEvent.keyDown(window) in tests) that a
        // document-scoped listener would miss.
        window.addEventListener('keydown', onKeyDown, true)
        return () => {
            window.removeEventListener('keydown', onKeyDown, true)
            previouslyFocused?.focus?.()
        }
    }, [isOpen])

    return { dialogRef }
}
